/**
 * One Speedy in the full-screen player (F-129; A plan §4.4, §4.10; A3-ALIGN §2.11).
 *
 * States, all in one box so nothing jumps: not playable yet ("still processing"), loading (poster
 * over the blurhash, "Loading Speedy…"), playing, buffering (a spinner after 500 ms of stall),
 * autoplay refused (a "Play" button), not available (404/403, no Retry), failed (Retry remounts the
 * pass hook), sign-in expired.
 *
 * Only the current slide plays. The next one loads 10 s and waits; the previous keeps its back
 * buffer. Where the browser has no Media Source (older iPhones), only the current slide has a
 * video at all.
 *
 * Progress: 3 s, 25% and 100% are recorded as views; 60% asks for the completion, and 100% asks
 * again if 60% wasn't counted. The 3-second view is always sent before a completion.
 */
import {
  isHLSProvider,
  MediaPlayer,
  type MediaPlayerInstance,
  MediaProvider,
  type MediaProviderAdapter,
} from '@vidstack/react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Loader2, Play } from 'lucide-react';
import { useStreamPass } from '@/hooks/use-stream-pass';
import { MediaImage } from '@/components/media/MediaImage';
import { MediaErrorState } from '@/components/media/MediaErrorState';
import { RetryKey } from '@/components/media/RetryKey';
import { configureHlsProvider } from '@/components/media/hls-provider';
import { resolveMediaUrl } from '@/lib/media';
import { api } from '@/api/client';
import { recordSpeedyCompletion, recordSpeedyView, type SpeedyCard } from '@/api/speedies';
import { SPEEDY_HLS_CONFIG } from './speedy-hls-config';
import { segmentStore } from './segment-store';
import Hls from 'hls.js';

export type SlideRole = 'current' | 'next' | 'prev';

/** True where hls.js can run (Media Source). Without it, only the current slide gets a video. */
export const CAN_PRELOAD = typeof window !== 'undefined' &&
  ('MediaSource' in window || 'ManagedMediaSource' in window);

function deviceKind(): 'ios' | 'android' | 'desktop' | 'other' {
  if (typeof navigator === 'undefined') return 'other';
  const ua = navigator.userAgent;
  if (/iPhone|iPad|iPod/.test(ua)) return 'ios';
  if (/Android/.test(ua)) return 'android';
  if (/Windows|Macintosh|Linux/.test(ua)) return 'desktop';
  return 'other';
}

/** Autoplay refused by the browser (as opposed to a load failure, which isn't a reason to tap). */
function isAutoplayRefusal(err: unknown): boolean {
  return (err as { name?: string } | null)?.name === 'NotAllowedError';
}

/** Which of the pass hook's errors means what, for a Speedy. */
export function classifyPassError(error: string): { kind: 'unavailable' | 'failed'; message: string } {
  if (/not available to you/i.test(error)) return { kind: 'unavailable', message: "This Speedy isn't available" };
  if (/session expired|not signed in/i.test(error)) return { kind: 'unavailable', message: 'Sign in again to watch' };
  return { kind: 'failed', message: "Couldn't play this Speedy" };
}

export interface SlideProgressHandlers {
  /** On screen 1 s or more. */
  onViewed?: (speedyId: string) => void;
  onCompleted?: (speedyId: string, points: number) => void;
  /** The furthest point reached, 0..1, reported as it grows. */
  onReach?: (speedyId: string, fraction: number) => void;
  onEnded?: (speedyId: string) => void;
}

interface SpeedySlideProps extends SlideProgressHandlers {
  card: SpeedyCard;
  role: SlideRole;
  muted: boolean;
  /** Loop at the end (the last Speedy of a list with nothing after it). */
  loop: boolean;
  /** A sheet or panel is over the video: pause. */
  suspended?: boolean;
}

export function SpeedySlide(props: SpeedySlideProps) {
  const { card, role } = props;
  const aspect = card.width && card.height ? card.width / card.height : 9 / 16;
  const poster = resolveMediaUrl(card.poster_url) ?? null;
  const upright = aspect < 0.8;
  // One live player, for the Speedy on screen (Tim, 2 Nov: phones have few video decoders). The
  // next ones start from their saved opening seconds (segment-store.ts) instead of a second player.
  // A second player only for the next Speedy when its opening isn't saved (never more than two).
  const mountPlayer = role === 'current'
    || (role === 'next' && CAN_PRELOAD && !!card.media_id && !segmentStore.has(card.media_id));

  const backdrop = (
    <div className="absolute inset-0">
      {/* Sideways or square video sits over a blurred copy of its own poster (no second video). */}
      {!upright && poster && (
        <img src={poster} alt="" aria-hidden className="absolute inset-0 h-full w-full scale-110 object-cover opacity-60 blur-2xl" />
      )}
      <MediaImage
        src={poster}
        blurhash={card.blurhash}
        alt=""
        fit={upright ? 'cover' : 'contain'}
        className="absolute inset-0 h-full w-full"
      />
    </div>
  );

  // The member's own clip while it uploads: played from the file, never through Stream; no views
  // or completion are sent for it.
  if (card.local_clip_url) {
    return (
      <div className="relative h-full w-full bg-black">
        {props.role === 'current' && (
          <video
            src={card.local_clip_url}
            autoPlay
            playsInline
            muted={props.muted}
            loop
            controls
            className={`absolute inset-0 h-full w-full ${upright ? 'object-cover' : 'object-contain'}`}
            aria-label={card.title ?? 'Your Speedy'}
          />
        )}
      </div>
    );
  }

  if (!card.playable || !card.media_id) {
    return (
      <div className="relative h-full w-full bg-black">
        {backdrop}
        <Centered>
          <MediaErrorState kind="processing" aspect={aspect} message="This Speedy is still processing" className="!bg-transparent" />
        </Centered>
      </div>
    );
  }

  return (
    <div className="relative h-full w-full bg-black">
      {backdrop}
      {mountPlayer && (
        <RetryKey>
          {(_attempt, retry) => <StreamPlayer {...props} aspect={aspect} upright={upright} poster={poster} onRetry={retry} />}
        </RetryKey>
      )}
    </div>
  );
}

function StreamPlayer({
  card,
  role,
  muted,
  loop,
  suspended,
  aspect,
  upright,
  poster,
  onRetry,
  onViewed,
  onCompleted,
  onReach,
  onEnded,
}: SpeedySlideProps & { aspect: number; upright: boolean; poster: string | null; onRetry: () => void }) {
  const playerRef = useRef<MediaPlayerInstance>(null);
  const resumeRef = useRef<{ at: number; playing: boolean } | null>(null);
  const renewals = useRef(0);
  const mediaId = card.media_id as string;
  const isCurrent = role === 'current';

  const { source, error, unavailable, renewOnError } = useStreamPass({
    resourceId: mediaId,
    tokenUrl: (base) => `${base}/functions/v1/media-stream?action=token&id=${encodeURIComponent(mediaId)}`,
    onBeforeSwap: () => {
      renewals.current += 1;
      const p = playerRef.current;
      resumeRef.current = { at: p?.currentTime ?? 0, playing: !(p?.paused ?? true) };
    },
    enabled: !!mediaId,
  });
  // A Speedy whose opening was saved starts from the saved address at once (no wait for a new
  // pass, and it works with no signal); after an error it moves to the live pass.
  // Read once when the player mounts: the entry may expire while this Speedy plays.
  const [savedUrl, setSavedUrl] = useState<string | null>(() => segmentStore.masterUrl(mediaId));
  const startedFromSaved = useRef(savedUrl !== null);
  const src = useMemo(
    () => (savedUrl
      ? { src: savedUrl, type: 'application/x-mpegurl' as const }
      : source ? { src: source.src, type: source.type } : null),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [savedUrl, source?.src, source?.type],
  );
  // Held at the saved 240p for the saved seconds, so the next piece isn't asked for at a quality
  // that wasn't saved; then hls.js chooses freely.
  const hlsRef = useRef<Hls | null>(null);
  const capped = useRef(false);

  const [started, setStarted] = useState(false);
  const [buffering, setBuffering] = useState(false);
  const [needsTap, setNeedsTap] = useState(false);
  const bufferTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const stats = useRef({
    becameCurrentAt: 0,
    startedAt: '',
    ttff: null as number | null,
    stalls: 0,
    stallMs: 0,
    stallSince: 0,
    errors: 0,
    fromPreload: false,
    engine: 'native' as 'hlsjs' | 'native',
    mountedAsPreload: !isCurrent,
  });

  const progress = useRef({
    viewSent: false,
    view3: null as Promise<unknown> | null,
    q25: false,
    q100: false,
    completion: 'none' as 'none' | 'asked60' | 'done',
    /** A completion request is on its way (60% and 100% never overlap). */
    asking: false,
    maxFraction: 0,
  });

  const sendView = useCallback((checkpoint: 3 | 25 | 100) => {
    const p = recordSpeedyView(card.id, checkpoint).catch(() => {});
    if (checkpoint === 3) progress.current.view3 = p;
    return p;
  }, [card.id]);

  const askCompletion = useCallback(async () => {
    const pr = progress.current;
    if (pr.completion === 'done' || pr.asking) return;
    pr.asking = true;
    try {
      if (pr.view3) await pr.view3;
      const res = await recordSpeedyCompletion(card.id);
      if (res.completed) {
        pr.completion = 'done';
        onCompleted?.(card.id, res.points);
      }
    } catch {
      // Asked again at 100%.
    } finally {
      pr.asking = false;
    }
  }, [card.id, onCompleted]);

  const onTimeUpdate = useCallback((currentTime: number) => {
    if (!isCurrent) return;
    // An unknown length (Infinity or NaN while loading) falls back to the card's.
    const playerDuration = playerRef.current?.duration;
    const duration = playerDuration && Number.isFinite(playerDuration) ? playerDuration : card.duration_s ?? 0;
    if (!duration || !Number.isFinite(duration)) return;
    const fraction = Math.min(currentTime / duration, 1);
    const pr = progress.current;
    // Past the saved opening: let hls.js choose the quality again.
    if (capped.current && currentTime >= 3 && hlsRef.current) {
      hlsRef.current.autoLevelCapping = -1;
      capped.current = false;
    }
    if (fraction > pr.maxFraction) {
      pr.maxFraction = fraction;
      onReach?.(card.id, fraction);
    }
    if (!pr.viewSent && currentTime >= 3) {
      pr.viewSent = true;
      void sendView(3);
    }
    if (pr.viewSent && !pr.q25 && fraction >= 0.25) {
      pr.q25 = true;
      void sendView(25);
    }
    if (pr.viewSent && pr.completion === 'none' && fraction >= 0.6) {
      pr.completion = 'asked60';
      void askCompletion();
    }
    if (pr.viewSent && !pr.q100 && fraction >= 0.99) {
      pr.q100 = true;
      // The 3-second view is sent again first: if it was lost, the completion would be refused.
      void (pr.completion === 'done' ? sendView(100) : sendView(3).then(() => sendView(100))).then(() => {
        if (progress.current.completion !== 'done') void askCompletion();
      });
    }
  }, [isCurrent, card.id, card.duration_s, onReach, sendView, askCompletion]);

  const tryPlay = useCallback(() => {
    const p = playerRef.current;
    if (!p) return;
    Promise.resolve(p.play())
      .then(() => setNeedsTap(false))
      .catch((err) => {
        if (isAutoplayRefusal(err)) setNeedsTap(true);
      });
  }, []);

  // Play only the current slide, and not while a sheet is over it. Before the media can play,
  // `onCanPlay` starts it instead (calling play() too early isn't a refusal).
  const canPlayRef = useRef(false);
  useEffect(() => {
    const p = playerRef.current;
    if (!p || !src) return;
    if (isCurrent && !suspended) {
      if (canPlayRef.current) tryPlay();
    } else {
      p.pause();
    }
  }, [isCurrent, suspended, src, tryPlay]);

  useEffect(() => {
    if (playerRef.current) playerRef.current.muted = muted;
  }, [muted]);

  // Back from another app or tab: start again (or show "Tap to play" if the browser refuses).
  useEffect(() => {
    if (!isCurrent) return;
    // Only if it was playing when the page went away (a Speedy the viewer paused stays paused).
    let wasPlaying = false;
    const onVis = () => {
      if (document.visibilityState === 'hidden') {
        wasPlaying = !(playerRef.current?.paused ?? true);
        return;
      }
      if (wasPlaying && !suspended && canPlayRef.current && playerRef.current?.paused) tryPlay();
      wasPlaying = false;
    };
    document.addEventListener('visibilitychange', onVis);
    window.addEventListener('pageshow', onVis);
    return () => {
      document.removeEventListener('visibilitychange', onVis);
      window.removeEventListener('pageshow', onVis);
    };
  }, [isCurrent, suspended, tryPlay]);

  // Becoming current starts the clock and the 1-second "viewed" mark; leaving writes the summary.
  useEffect(() => {
    if (!isCurrent) return;
    const st = stats.current;
    st.becameCurrentAt = performance.now();
    st.startedAt = new Date().toISOString();
    // "Preloaded" now also means it started from a saved opening, so the effect is measurable.
    st.fromPreload = st.mountedAsPreload || startedFromSaved.current;
    const viewedTimer = setTimeout(() => onViewed?.(card.id), 1000);
    return () => {
      clearTimeout(viewedTimer);
      if (st.stallSince) st.stallMs += performance.now() - st.stallSince;
      api.insertPlaybackSession({
        speedy_id: card.id,
        started_at: st.startedAt,
        ended_at: new Date().toISOString(),
        ttff_ms: st.ttff,
        stall_count: st.stalls,
        stall_ms: st.stallMs,
        from_preload: st.fromPreload,
        start_level: null,
        max_level: null,
        pass_renewals: renewals.current,
        errors: st.errors,
        engine: st.engine,
        device: deviceKind(),
      });
      Object.assign(st, { ttff: null, stalls: 0, stallMs: 0, stallSince: 0, errors: 0, mountedAsPreload: true });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCurrent, card.id]);

  useEffect(() => () => {
    if (bufferTimer.current) clearTimeout(bufferTimer.current);
  }, []);

  const onProviderChange = useCallback((provider: MediaProviderAdapter | null) => {
    const fromSaved = startedFromSaved.current;
    if (provider && isHLSProvider(provider)) {
      stats.current.engine = 'hlsjs';
      provider.onInstance((hls) => {
        hlsRef.current = hls as unknown as Hls;
        if (!fromSaved) return;
        // Hold the saved quality for the saved seconds (released at 3 s, see onTimeUpdate).
        hls.once(Hls.Events.MANIFEST_PARSED, () => {
          hls.autoLevelCapping = 0;
          capped.current = true;
        });
      });
    }
    // hls.js sorts qualities smallest first, so the saved 240p is level 0; set before hls.js starts.
    configureHlsProvider(provider, fromSaved ? { ...SPEEDY_HLS_CONFIG, startLevel: 0 } : SPEEDY_HLS_CONFIG);
  }, []);

  if (unavailable && !savedUrl) {
    return (
      <Centered>
        <MediaErrorState kind="processing" aspect={aspect} message="This Speedy is still processing" className="!bg-transparent" />
      </Centered>
    );
  }
  if (error && !savedUrl) {
    const e = classifyPassError(error);
    return (
      <Centered>
        <MediaErrorState kind={e.kind} aspect={aspect} posterUrl={poster} message={e.message} onRetry={onRetry} className="!bg-transparent" />
      </Centered>
    );
  }

  return (
    <>
      {src && (
        <MediaPlayer
          ref={playerRef}
          src={src}
          muted={muted}
          playsInline
          preload={isCurrent ? 'auto' : 'metadata'}
          loop={loop}
          className={`absolute inset-0 h-full w-full ${upright ? '[&_video]:object-cover' : '[&_video]:object-contain'}`}
          onProviderChange={onProviderChange}
          onCanPlay={() => {
            canPlayRef.current = true;
            const r = resumeRef.current;
            const p = playerRef.current;
            // After a pass swap, carry on only if it was playing (a paused Speedy stays paused).
            const resume = r ? r.playing : true;
            if (r && p) {
              p.currentTime = r.at;
              resumeRef.current = null;
            }
            if (isCurrent && !suspended && resume) tryPlay();
          }}
          onPlaying={() => {
            setStarted(true);
            setBuffering(false);
            if (bufferTimer.current) clearTimeout(bufferTimer.current);
            const st = stats.current;
            if (st.ttff === null && st.becameCurrentAt) st.ttff = performance.now() - st.becameCurrentAt;
            if (st.stallSince) {
              st.stallMs += performance.now() - st.stallSince;
              st.stallSince = 0;
            }
          }}
          onWaiting={() => {
            if (!isCurrent) return;
            const st = stats.current;
            if (started) {
              st.stalls += 1;
              st.stallSince = performance.now();
            }
            if (bufferTimer.current) clearTimeout(bufferTimer.current);
            bufferTimer.current = setTimeout(() => setBuffering(true), 500);
          }}
          onTimeUpdate={({ currentTime }) => onTimeUpdate(currentTime)}
          onEnded={() => onEnded?.(card.id)}
          onError={() => {
            stats.current.errors += 1;
            // No signal: keep the player and wait. Moving to a new pass can't work offline, and
            // would take the player away.
            if (typeof navigator !== 'undefined' && navigator.onLine === false) {
              setBuffering(true);
              window.addEventListener('online', () => hlsRef.current?.startLoad(), { once: true });
              return;
            }
            if (savedUrl) {
              // The saved address failed (its pass may have run out): carry on from the live pass.
              const p = playerRef.current;
              resumeRef.current = { at: p?.currentTime ?? 0, playing: !(p?.paused ?? true) };
              setSavedUrl(null);
              return;
            }
            renewOnError();
          }}
        >
          <MediaProvider className="h-full w-full [&_video]:h-full [&_video]:w-full" />
        </MediaPlayer>
      )}
      {isCurrent && !started && !needsTap && (
        <div className="pointer-events-none absolute inset-0 flex items-center justify-center" role="status">
          <span className="flex items-center gap-2 rounded-full bg-black/50 px-3 py-1.5 text-xs text-white">
            <Loader2 className="h-4 w-4 animate-spin" aria-hidden /> Loading Speedy…
          </span>
        </div>
      )}
      {isCurrent && buffering && started && (
        <div className="pointer-events-none absolute inset-0 flex items-center justify-center" role="status" aria-label="Buffering">
          <Loader2 className="h-8 w-8 animate-spin text-white/80" aria-hidden />
        </div>
      )}
      {isCurrent && needsTap && (
        <button
          type="button"
          onClick={() => {
            const p = playerRef.current;
            if (p) Promise.resolve(p.play()).then(() => setNeedsTap(false)).catch(() => {});
          }}
          className="absolute inset-0 flex items-center justify-center"
          aria-label="Play"
        >
          <span className="flex h-16 w-16 items-center justify-center rounded-full bg-black/50 text-white">
            <Play className="h-8 w-8" aria-hidden />
          </span>
        </button>
      )}
    </>
  );
}

function Centered({ children }: { children: React.ReactNode }) {
  return <div className="absolute inset-0 flex items-center justify-center">{children}</div>;
}
